import React, { useState } from 'react';
import './Help.css';

const Help: React.FC = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement actual form submission
    console.log('Contact form submitted:', contactForm);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setContactForm({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="help-page">
      <div className="help-header">
        <h1>Help & Support</h1>
        <p>Everything you need to know about Share My Shed</p>
      </div>

      <div className="help-content">
        {/* Getting Started Section */}
        <section className="help-section">
          <h2>Getting Started</h2>
          
          <div className="instruction-cards">
            <div className="instruction-card">
              <div className="instruction-icon">📝</div>
              <h3>Listing an Item</h3>
              <ol>
                <li>Click on "My Items" in the sidebar</li>
                <li>Click the "Add New Item" button</li>
                <li>Fill in your item details:
                  <ul>
                    <li>Add clear photos of your item</li>
                    <li>Write a detailed description</li>
                    <li>Set a fair daily rate</li>
                    <li>Choose the right category</li>
                    <li>Set minimum and maximum rental days</li>
                  </ul>
                </li>
                <li>Add your location details</li>
                <li>Click "Save" to list your item</li>
              </ol>
              <div className="tip">
                <strong>💡 Tip:</strong> Good photos and detailed descriptions help your items get rented faster!
              </div>
            </div>

            <div className="instruction-card">
              <div className="instruction-icon">🛒</div>
              <h3>Renting an Item</h3>
              <ol>
                <li>Browse categories or search for items</li>
                <li>Click on an item to view details</li>
                <li>Check availability and pricing</li>
                <li>Select your rental dates</li>
                <li>Add to your wheelbarrow (cart)</li>
                <li>Proceed to checkout</li>
                <li>Choose your payment method (credit/debit card)</li>
                <li>Complete payment and arrange pickup</li>
              </ol>
              <div className="tip">
                <strong>💡 Tip:</strong> Message the owner before renting to discuss pickup arrangements!
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="help-section">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-list">
            <div className="faq-item">
              <h4>How do I set a fair price for my item?</h4>
              <p>Research similar items in your area, consider the item's value, and factor in wear and tear. Generally, 5-15% of the item's retail value per day is reasonable.</p>
            </div>
            
            <div className="faq-item">
              <h4>What if an item gets damaged during rental?</h4>
              <p>Renters are responsible for returning items in the same condition. Document any existing wear before lending, and both parties should inspect items during pickup and return.</p>
            </div>
            
            <div className="faq-item">
              <h4>How do I arrange pickup and return?</h4>
              <p>Use the messaging system to coordinate with the other party. Agree on pickup times, location, and return arrangements before confirming the rental.</p>
            </div>
            
            <div className="faq-item">
              <h4>What payment methods are accepted?</h4>
              <p>We accept major credit and debit cards. All payments are processed securely through our trusted payment partners.</p>
            </div>
            
            <div className="faq-item">
              <h4>Can I cancel a rental?</h4>
              <p>Cancellation policies vary by owner. Check the item listing for cancellation terms or message the owner directly to discuss changes.</p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="help-section">
          <h2>Contact Us</h2>
          
          <div className="contact-content">
            <div className="contact-info">
              <h3>Get in Touch</h3>
              <p>Have a question that's not answered above? We're here to help!</p>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <span className="contact-icon">📧</span>
                  <div>
                    <strong>Email</strong>
                    <p>support@sharemyshed.com</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <span className="contact-icon">📱</span>
                  <div>
                    <strong>Phone</strong>
                    <p>+44 20 1234 5678</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <span className="contact-icon">⏰</span>
                  <div>
                    <strong>Support Hours</strong>
                    <p>Monday - Friday: 9AM - 6PM GMT</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h3>Send us a Message</h3>
              
              {isSubmitted ? (
                <div className="form-success">
                  <div className="success-icon">✅</div>
                  <h4>Message Sent!</h4>
                  <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <select
                      id="subject"
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleContactChange}
                      required
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Question</option>
                      <option value="technical">Technical Support</option>
                      <option value="payment">Payment Issues</option>
                      <option value="dispute">Rental Dispute</option>
                      <option value="suggestion">Feature Suggestion</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactChange}
                      rows={5}
                      placeholder="Please describe your question or issue in detail..."
                      required
                    />
                  </div>

                  <button type="submit" className="submit-button">
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Social Links Section */}
        <section className="help-section">
          <h2>Connect With Us</h2>
          
          <div className="social-links">
            <a href="https://twitter.com/sharemyshed" target="_blank" rel="noopener noreferrer" className="social-link twitter">
              <span className="social-icon">🐦</span>
              <div>
                <strong>Twitter</strong>
                <p>Follow us for updates and tips</p>
              </div>
            </a>
            
            <a href="https://facebook.com/sharemyshed" target="_blank" rel="noopener noreferrer" className="social-link facebook">
              <span className="social-icon">📘</span>
              <div>
                <strong>Facebook</strong>
                <p>Join our community</p>
              </div>
            </a>
            
            <a href="https://instagram.com/sharemyshed" target="_blank" rel="noopener noreferrer" className="social-link instagram">
              <span className="social-icon">📷</span>
              <div>
                <strong>Instagram</strong>
                <p>See items and success stories</p>
              </div>
            </a>
            
            <a href="https://linkedin.com/company/sharemyshed" target="_blank" rel="noopener noreferrer" className="social-link linkedin">
              <span className="social-icon">💼</span>
              <div>
                <strong>LinkedIn</strong>
                <p>Professional updates</p>
              </div>
            </a>
            
            <a href="https://discord.gg/sharemyshed" target="_blank" rel="noopener noreferrer" className="social-link discord">
              <span className="social-icon">🎮</span>
              <div>
                <strong>Discord</strong>
                <p>Chat with other users</p>
              </div>
            </a>
            
            <a href="https://github.com/sharemyshed" target="_blank" rel="noopener noreferrer" className="social-link github">
              <span className="social-icon">🔧</span>
              <div>
                <strong>GitHub</strong>
                <p>Open source development</p>
              </div>
            </a>
          </div>
        </section>

        {/* Safety Tips */}
        <section className="help-section">
          <h2>Safety & Best Practices</h2>
          
          <div className="safety-tips">
            <div className="safety-tip">
              <h4>🔒 For Item Owners</h4>
              <ul>
                <li>Meet renters in person before handing over valuable items</li>
                <li>Take photos of your item's condition before lending</li>
                <li>Verify the renter's identity and contact information</li>
                <li>Set clear expectations about care and usage</li>
                <li>Consider requiring a deposit for expensive items</li>
              </ul>
            </div>
            
            <div className="safety-tip">
              <h4>🛡️ For Renters</h4>
              <ul>
                <li>Inspect items carefully before taking them</li>
                <li>Ask questions about proper usage and care</li>
                <li>Return items on time and in good condition</li>
                <li>Report any issues immediately</li>
                <li>Be respectful of other people's property</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Help; 